import { useState, useEffect, useCallback } from 'react';
import { Folder, File as FileIcon, ChevronRight, Loader2, RefreshCw, AlertTriangle, Check, FolderTree } from 'lucide-react';
import { apiService, PackFileNode, PackContainer } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import chipStyles from '@/components/config/GroupPicker.module.scss';
import styles from './FilesPage.module.scss';

const TEXT_EXT = ['json', 'lang', 'molang', 'txt', 'properties', 'yaml', 'yml', 'conf', 'config', 'cfg', 'toml', 'ini', 'md', 'csv', 'js', 'ts', 'mcfunction'];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function ServersPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [containers, setContainers] = useState<PackContainer[]>([]);
  const [id, setId] = useState('');
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<PackFileNode[]>([]);
  const [selected, setSelected] = useState<PackFileNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setContainers(list);
      setId((prev) => (list.some((c) => c.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId]);

  const container = containers.find((c) => c.id === id);
  const rootLabel = container?.name || id;

  const loadDir = useCallback((cid: string, p: string) => {
    if (!cid) { setEntries([]); return; }
    setLoading(true);
    apiService.getContainerTree(cid, p).then((r) => { setEntries(r.data?.entries ?? []); setLoading(false); });
  }, []);

  useEffect(() => { setPath(''); setSelected(null); setStatus(null); }, [id]);
  useEffect(() => { setSelected(null); loadDir(id, path); }, [id, path, loadDir]);

  const sync = async () => {
    setSyncing(true);
    setStatus(null);
    const r = await apiService.syncContainer(id);
    setSyncing(false);
    setStatus(r.data ? { ok: r.data.success, msg: r.data.message } : { ok: false, msg: r.error ?? 'Échec de la synchronisation' });
    loadDir(id, path);
  };

  const segments = path ? path.split('/') : [];
  const busy = syncing;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Serveurs</h1>
          <p className={styles.subtitle}>Éditer les configs des containers de prod (data/mods). Enregistre via la barre du bas, puis envoie une version depuis « Envoyer ».</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.saveBtn} onClick={sync} disabled={!id || busy}>
            {syncing ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} {syncing ? 'Sync…' : 'Synchroniser'}
          </button>
        </div>
      </header>

      <div className={chipStyles.chips}>
        {containers.length === 0 ? (
          <span className={chipStyles.empty}>Aucun container pour ce projet</span>
        ) : (
          containers.map((c) => {
            const on = c.id === id;
            return (
              <button
                key={c.id}
                className={`${chipStyles.chip} ${on ? chipStyles.chipActive : ''}`}
                style={on ? { borderColor: '#F97316', backgroundColor: '#F9731622', color: '#F97316' } : undefined}
                onClick={() => setId(c.id)}
              >
                <span className={chipStyles.dot} style={{ backgroundColor: '#F97316' }} />
                {c.name}
              </button>
            );
          })
        )}
      </div>

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {id && (
        <div className={styles.breadcrumb}>
          <button className={styles.crumb} onClick={() => setPath('')}><FolderTree size={14} /> {rootLabel}</button>
          {segments.map((seg, i) => (
            <span key={i} className={styles.crumbGroup}>
              <ChevronRight size={13} className={styles.crumbSep} />
              <button className={styles.crumb} onClick={() => setPath(segments.slice(0, i + 1).join('/'))}>{seg}</button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.split}>
        <div className={styles.listCol}>
          {loading ? (
            <div className={styles.listEmpty}><Loader2 size={18} className={styles.spin} /></div>
          ) : entries.length === 0 ? (
            <div className={styles.listEmpty}>Dossier vide — clique « Synchroniser » pour récupérer les fichiers du container.</div>
          ) : (
            entries.map((e) => (
              <button
                key={e.path}
                className={`${styles.row} ${!e.dir && selected?.path === e.path ? styles.rowActive : ''}`}
                onClick={() => (e.dir ? setPath(e.path) : setSelected(e))}
              >
                {e.dir ? <Folder size={16} className={styles.folderIcon} /> : <FileIcon size={16} className={styles.fileIcon} />}
                <span className={styles.rowName}>{e.name}</span>
                {!e.dir && <span className={styles.rowSize}>{formatSize(e.size)}</span>}
              </button>
            ))
          )}
        </div>

        <div className={styles.detail} key={selected?.path}>
          {selected ? <ConfigEditor containerId={id} node={selected} /> : (
            <div className={styles.detailEmpty}>
              <FolderTree size={40} strokeWidth={1.5} />
              <p>Sélectionne un fichier de config pour l'éditer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Éditeur texte d'un fichier de config container (JSON validé côté serveur).
 * Enregistrement via la barre du bas (comme blocks/items) ; survit au changement de fichier. */
function ConfigEditor({ containerId, node }: { containerId: string; node: PackFileNode }) {
  const [text, setText] = useState('');
  const [orig, setOrig] = useState('');
  const [loading, setLoading] = useState(true);
  const editable = TEXT_EXT.includes(extOf(node.name));

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.getContainerFileRaw(containerId, node.path).then((t) => {
      if (!active) return;
      const disk = t ?? '';
      setOrig(disk);
      const pending = getDraftPayload(draftKey('container-file', containerId, node.path)) as string | undefined;
      setText(pending ?? disk);
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId, node.path]);

  const dirty = editable && !loading && text !== orig;

  const save = async () => {
    const res = await apiService.saveContainerFile(containerId, node.path, text);
    const ok = !!res.data?.success;
    if (ok) setOrig(text);
    return ok;
  };

  useSnapshotDraft({
    kind: 'container-file',
    groupId: containerId,
    identifier: node.path,
    label: `${containerId} · ${node.name}`,
    dirty,
    payload: text,
    save,
  });

  if (loading) return <div className={styles.viewer}><Loader2 size={18} className={styles.spin} /> Chargement…</div>;
  if (!editable) return <div className={styles.viewer}><div className={styles.viewerHead}>{node.path}</div><p className={styles.viewerNote}>Type de fichier non éditable ici.</p></div>;

  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHead}>{node.path}</div>
      <textarea className={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      <p className={styles.viewerNote}>
        {dirty ? 'Modifications en attente — enregistre-les via la barre du bas, puis « Déployer ».' : 'Enregistré. Clique « Déployer » pour publier une version.'}
      </p>
    </div>
  );
}
